// Awizacja (Peute delivery-advice) import — shared types.
//
// Isomorphic (FE + Deno). The parser produces `RawAwizacjaRow[]` straight from
// the .xlsx; the mapper turns those into `TransactionCandidate[]` (resolved
// against the company/product database). The FE review grid consumes the
// candidates + the label maps and, on approve, writes transactions +
// transaction_transports.

export type MatchState = "exact" | "fuzzy" | "conflict" | "unmatched" | "alias";
export type Means = "truck" | "ship" | "train";
export type CarrierRole = "5a" | "5b" | "5c";

// ─── Learned aliases (stored in the company_source_aliases table) ─────────
// A source string → company id, by the signal it carries: `name` (any
// name-identified party — unload place, carriers) or `address` (load place,
// where the vendor name repeats across sites). Confirmed on approve so future
// imports resolve the same source without guessing.
export type AliasKind = "name" | "address";
export type AliasMap = Record<AliasKind, Record<string, number>>;
export const EMPTY_ALIASES: AliasMap = { name: {}, address: {} };

// ─── Raw (straight from Excel) ────────────────────────────────────────────

export interface RawCarrierLeg {
  role: CarrierRole;
  name: string;
  nip: string | null;
  driver: string | null;
  driverPhone: string | null;
  plate: string | null;
  means: Means;
}

export interface RawAwizacjaRow {
  sourceFile: string;
  peuteRef: string; // → external_id
  orderNumber: string; // Purchase Document No → order_number
  collectionDate: string | null; // ISO
  timeslot: string | null;
  itemDesc: string; // waste description (OCC / Cores)
  vendorName: string; // Vendor Name (reference only)
  depCountry: string;
  depZip: string;
  depCity: string;
  depStreet: string;
  recoveryFacility: string; // → unload place
  container: string;
  size: string;
  shipowner: string;
  carriers: RawCarrierLeg[];
}

// ─── Mapped candidate (the review-grid row) ───────────────────────────────

export interface CandidateCarrierLeg {
  role: CarrierRole;
  rawName: string;
  nip: string | null;
  driver: string | null;
  driverPhone: string | null;
  plate: string | null;
  means: Means;
  companyId: number | null;
  match: MatchState;
}

export interface TransactionCandidate {
  id: string; // grid row key — peuteRef, suffixed `#n` when lines share one
  sourceFile: string;
  externalId: string;
  orderNumber: string;
  container: string;
  size: string;
  shipowner: string;
  loadDate: string;
  unloadDate: string;
  timeslot: string | null;
  // waste product
  productRaw: string;
  productId: number | null;
  productMatch: MatchState;
  // load place (resolved by departure address)
  loadPlaceRaw: string;
  loadPlaceId: number | null;
  loadPlaceMatch: MatchState;
  depCountry: string;
  depZip: string;
  depCity: string;
  depStreet: string;
  // unload place / importer / commercial
  unloadPlaceRaw: string;
  unloadPlaceId: number | null;
  unloadPlaceMatch: MatchState;
  importerId: number | null;
  unloadCommercialId: number | null;
  // carrier chain (Annex VII block 5)
  carriers: CandidateCarrierLeg[];
  // detail-mismatch warnings from the mapper (matched company, but its NIP /
  // address differs from the Excel) — keyed by field. Cleared per field once
  // the operator manually re-picks that cell.
  matchWarnings: { field: string; message: string }[];
  // lifecycle (set by the FE after a successful create)
  createdRef: string | null;
  createdId: number | null;
}

export interface CompanyLabel {
  short_name: string;
  city: string | null;
  nip: string | null;
}
export interface ProductLabel {
  name: string;
  waste_code: string | null;
  basel_code: string | null;
}

export interface AwizacjaImportResult {
  candidates: TransactionCandidate[];
  /** Labels for every company id the mapper resolved — lets the grid render
   *  names + seed EntitySelect `extraOption` without its own fetch. */
  companyLabels: Record<number, CompanyLabel>;
  productLabels: Record<number, ProductLabel>;
}
